// TapeStatsBar — at-a-glance summary above the live feed.
// Counts opens / views / scrolls / copies in the current event window,
// breaks classification (bot vs human vs hot), shows top-3 hottest recipients.
//
// v3 additions:
//   · Hour rate KPI — events in the last hour vs the prior hour, with arrow.
//   · Unique recipients KPI — distinct contact_email / recipient values.
//   · Recipient rows are clickable, surfacing the per-recipient timeline.
//
// [hardening 3x] additions:
//   · Strip chip showing "X unique humans · Y prefetch · Z bots · W scanners"
//     (sourced from event.ua_bucket / event.open_lifecycle).
//   · Click-through rate (clicks / unique_opens).
//   · Forward rate (forwarded_opens / unique_opens).

import { toMs } from '../hooks/useEventFilter.js';

const TYPE_BUCKETS = {
  opens:   ['email_open','email_pixel','pixel_open'],
  views:   ['view_open','view_visit','view_pageshow'],
  scrolls: ['view_scroll'],
  copies:  ['view_copy'],
  clicks:  ['link_click','link_redirect'],
};

const HOUR_MS = 60 * 60 * 1000;

function classify(events, classifications) {
  let bots = 0, humans = 0, hot = 0;
  for (const e of events) {
    const c = e.classification;
    if (!c) continue;
    if (classifications.bot.includes(c)) bots++;
    else if (classifications.human.includes(c)) humans++;
    if (c === 'active_reading') hot++;
  }
  return { bots, humans, hot };
}

// [hardening 3x] Aggregate the new ua_bucket + open_lifecycle fields written
// by _ingest.js / onEventCreate. Falls back gracefully when older events
// without these fields are mixed in.
function classifySplits(events) {
  let unique_humans = 0, prefetch = 0, bots = 0, scanners = 0;
  let unique_opens = 0, forwarded_opens = 0, clicks = 0;
  for (const e of events) {
    // Splits row uses ua_bucket (every event has it after the hardening pass).
    const b = e.ua_bucket;
    if (b === 'prefetch') prefetch++;
    else if (b === 'bot') bots++;
    else if (b === 'scanner') scanners++;
    else if (e.ua_is_real_open) unique_humans++;

    // Open lifecycle aggregates (only set for open-like event types).
    const ol = e.open_lifecycle;
    if (ol === 'likely_human_open' || ol === 'confirmed_human_open') unique_opens++;
    else if (ol === 'forwarded_open' || ol === 'forwarded_burst') forwarded_opens++;
    else if (e.open_unique) unique_opens++;
    else if (e.open_forwarded) forwarded_opens++;

    if (TYPE_BUCKETS.clicks.includes(e.type)) clicks++;
  }
  return { unique_humans, prefetch, bots, scanners, unique_opens, forwarded_opens, clicks };
}

function recipientKey(e) {
  return e.contact_email || e.recipient || e.recipient_email || e.recipient_view_id || e.view_id || null;
}

function topRecipients(events, n = 3) {
  const map = new Map();
  for (const e of events) {
    const k = recipientKey(e) || 'unknown';
    if (!map.has(k)) map.set(k, { email: k, opens: 0, views: 0, dwell: 0, last: null });
    const r = map.get(k);
    if (TYPE_BUCKETS.opens.includes(e.type)) r.opens++;
    if (TYPE_BUCKETS.views.includes(e.type)) r.views++;
    if (typeof e.dwell_ms === 'number') r.dwell += e.dwell_ms;
    if (typeof e.total_dwell_ms === 'number') r.dwell = Math.max(r.dwell, e.total_dwell_ms);
    const ms = toMs(e.occurred_at);
    if (!r.last || ms > r.last) r.last = ms;
  }
  return [...map.values()]
    .sort((a, b) => (b.opens + b.views) - (a.opens + a.views) || b.dwell - a.dwell)
    .slice(0, n);
}

function uniqueRecipients(events) {
  const set = new Set();
  for (const e of events) {
    const k = recipientKey(e);
    if (k) set.add(k);
  }
  return set.size;
}

function hourRate(events, now = Date.now()) {
  const lastHour = now - HOUR_MS;
  const priorHour = now - 2 * HOUR_MS;
  let cur = 0, prev = 0;
  for (const e of events) {
    const t = toMs(e.occurred_at);
    if (t >= lastHour) cur++;
    else if (t >= priorHour) prev++;
  }
  let trend = 'flat';
  if (cur > prev) trend = 'up';
  else if (cur < prev) trend = 'down';
  const delta = cur - prev;
  return { cur, prev, trend, delta };
}

const fmtSec = (ms) => {
  if (!ms || ms < 1000) return '—';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

const fmtPct = (num, den) => {
  if (!den) return '—';
  const v = (num / den) * 100;
  if (!Number.isFinite(v)) return '—';
  return `${v >= 10 ? Math.round(v) : v.toFixed(1)}%`;
};

export function TapeStatsBar({ events = [], onRecipientClick }) {
  const counts = {};
  for (const k of Object.keys(TYPE_BUCKETS)) counts[k] = 0;
  for (const e of events) {
    for (const [bucket, types] of Object.entries(TYPE_BUCKETS)) {
      if (types.includes(e.type)) counts[bucket]++;
    }
  }

  const cls = classify(events, {
    bot: ['bot','prefetch','apple_prefetch','likely_bot'],
    human: ['likely_human','confirmed_human','active_reading'],
  });

  const splits = classifySplits(events);
  const ctr = fmtPct(splits.clicks, splits.unique_opens);
  const fwdRate = fmtPct(splits.forwarded_opens, splits.unique_opens);

  const top = topRecipients(events, 3);
  const uniq = uniqueRecipients(events);
  const rate = hourRate(events);

  const Card = ({ label, value, accent, sub }) => (
    <div style={{
      flex: 1, minWidth: 110, padding: '10px 14px',
      background: 'var(--card)', border: '1px solid var(--rule)',
      borderRadius: 'var(--radius)',
    }}>
      <div className="eyebrow" style={{ fontSize: 9, marginBottom: 2 }}>{label}</div>
      <div style={{
        fontFamily: 'var(--display-font)',
        fontSize: 22, fontWeight: 700,
        color: accent || 'var(--ink)',
        lineHeight: 1.1,
      }}>
        {value}
      </div>
      {sub != null && (
        <div style={{ marginTop: 2, fontSize: 10, color: 'var(--muted)' }}>{sub}</div>
      )}
    </div>
  );

  const Chip = ({ label, value, color }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', fontSize: 11,
      border: '1px solid var(--rule)',
      borderRadius: 999,
      background: 'var(--card)',
      color: color || 'var(--body)',
      fontFamily: 'var(--mono-font)',
    }}>
      <span style={{ fontWeight: 600 }}>{value}</span>
      <span style={{ color: 'var(--muted)', fontSize: 10, letterSpacing: '0.04em' }}>{label}</span>
    </span>
  );

  const trendArrow = rate.trend === 'up' ? '▲' : rate.trend === 'down' ? '▼' : '→';
  const trendColor = rate.trend === 'up' ? 'var(--positive)'
                    : rate.trend === 'down' ? 'var(--alert)'
                    : 'var(--muted)';
  const rateSub = (
    <span style={{ color: trendColor }}>
      {trendArrow} {rate.delta >= 0 ? '+' : ''}{rate.delta} vs prior hour ({rate.prev})
    </span>
  );

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <Card label="Hour rate"      value={rate.cur} sub={rateSub} accent="var(--accent)" />
        <Card label="Unique recips"  value={uniq} sub={`${events.length} total events`} />
        <Card label="Opens"          value={counts.opens} />
        <Card label="View visits"    value={counts.views} />
        <Card label="Scrolls"        value={counts.scrolls} />
        <Card label="Clicks"         value={counts.clicks} />
        <Card label="Copies"         value={counts.copies} accent="var(--accent)" />
        <Card label="Hot"            value={cls.hot}    accent="var(--alert)" />
        <Card label="Bots"           value={cls.bots}   accent="var(--dim)" />
        <Card label="Humans"         value={cls.humans} accent="var(--positive)" />
      </div>

      {/* [hardening 3x] UA + lifecycle splits strip. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10,
        padding: '6px 8px', background: 'var(--sunken)',
        border: '1px solid var(--rule)', borderRadius: 'var(--radius)',
      }}>
        <Chip label="unique humans"  value={splits.unique_humans}     color="var(--positive)" />
        <Chip label="prefetch"       value={splits.prefetch}          color="var(--dim)" />
        <Chip label="bots"           value={splits.bots}              color="var(--dim)" />
        <Chip label="scanners"       value={splits.scanners}          color="var(--alert)" />
        <Chip label="unique opens"   value={splits.unique_opens}      color="var(--ink)" />
        <Chip label="forwarded"      value={splits.forwarded_opens}   color="var(--accent)" />
        <Chip label="CTR"            value={ctr}                      color="var(--accent)" />
        <Chip label="forward rate"   value={fwdRate}                  color="var(--accent)" />
      </div>

      {top.length > 0 && (
        <div style={{
          padding: '10px 14px', background: 'var(--sunken)',
          border: '1px solid var(--rule)', borderRadius: 'var(--radius)', fontSize: 12,
        }}>
          <div className="eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
            Hottest right now
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {top.map((r) => {
              const clickable = !!onRecipientClick;
              return (
                <div
                  key={r.email}
                  onClick={clickable ? () => onRecipientClick(r.email) : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); onRecipientClick(r.email);
                    }
                  } : undefined}
                  style={{
                    minWidth: 220,
                    cursor: clickable ? 'pointer' : 'default',
                    padding: clickable ? '4px 6px' : 0,
                    margin: clickable ? '-4px -6px' : 0,
                    borderRadius: 'var(--radius)',
                  }}
                  onMouseEnter={clickable ? (e) => {
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 8%, transparent)';
                  } : undefined}
                  onMouseLeave={clickable ? (e) => {
                    e.currentTarget.style.background = 'transparent';
                  } : undefined}
                >
                  <div style={{ fontFamily: 'var(--mono-font)', fontSize: 12, color: 'var(--ink)' }}>
                    {r.email}
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                    {r.opens} open{r.opens === 1 ? '' : 's'} · {r.views} view{r.views === 1 ? '' : 's'} · dwell {fmtSec(r.dwell)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
